import { Box, Slider, Stack } from '@mui/material';
import { useState, useCallback } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { ConfirmDialog } from '../common/dialogs/ConfirmDialog';
import { getCroppedImg } from './cropImage';
import { Typography } from '@sweep-io/sweep-design';

type UploadImageProps = {
  originSrc: string;
  onConfirmCb: (imageBase64Blob: string) => void;
  onCancelCb: () => void;
};

const UploadImage = ({ originSrc, onConfirmCb, onCancelCb }: UploadImageProps) => {
  const [src, setSrc] = useState<string | null>(originSrc);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCancel = () => {
    onCancelCb && onCancelCb();
    setSrc(null);
  };

  const handleConfirm = async () => {
    if (onConfirmCb && src && croppedAreaPixels) {
      const croppedImage = await getCroppedImg(src, croppedAreaPixels);
      if (croppedImage) {
        await onConfirmCb(croppedImage);
      }
    }
    setSrc(null);
  };

  return (
    <ConfirmDialog
      confirmText="Upload"
      onConfirm={handleConfirm}
      handleClose={handleCancel}
      isOpen={!!src}
      PaperProps={{ sx: { minWidth: 'auto' } }}
    >
      <Stack gap={2} data-testid="upload-image-dialog-content">
        <Box
          sx={{
            position: 'relative',
            width: 300,
            height: 300,
          }}
        >
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          )}
        </Box>
        <Stack direction="row" gap={2} alignItems="center">
          <Typography variant="body">Zoom</Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(_, value) => setZoom(value as number)}
            size="small"
          />
        </Stack>
      </Stack>
    </ConfirmDialog>
  );
};

export { UploadImage };
